import {usePromotionApi} from '~/composables/api/usePromotionApi.js'
import {useRolePermissions, actions, subjects} from '~/composables/useRolePermissions'
import {usePlanningAlert} from '~/composables/usePlanningAlert'

const sharedPromotions = ref([])
const sharedSelectedPromotion = ref(null)
const sharedLoadingPromotions = ref(false)
let loadPromise = null

export const usePromotionNavigation = () => {
  const route = useRoute()
  const {getPromotions} = usePromotionApi()
  const {can} = useRolePermissions()
  const authStore = useAuthStore()

  const secondaryMenuConfig = (basePath) => [
    {
      label: 'Resumen',
      to: basePath,
      icon: 'material-symbols:space-dashboard',
      exact: true,
      permission: [actions.VIEW, subjects.DASHBOARD]
    },
    {section: 'Gestión'},
    {
      label: 'Presupuesto',
      to: `${basePath}/detail`,
      icon: 'material-symbols:account-balance-wallet',
      permission: [actions.LIST, subjects.BUDGET]
    },
    {
      label: 'Planificación',
      to: `${basePath}/planning`,
      icon: 'mdi:chart-gantt',
      // Planificar es cosa de arquitecto y admin, no del contratista ni del promotor
      permission: [actions.VIEW, subjects.PROMOTION_CONFIG]
    },
    {
      label: 'Simulaciones',
      to: `${basePath}/simulations`,
      icon: 'mdi:flask-outline',
      // Mismo gate que Planificación: arquitecto, admin y developer (espejo del subject SIMULATIONS)
      permission: [actions.VIEW, subjects.SIMULATIONS]
    },
    {
      label: ['contractor_admin', 'contractor_user'].includes(authStore.role) ? 'Mis ofertas' : 'Ofertas recibidas',
      to: `${basePath}/quotes`,
      icon: 'material-symbols:sell',
      permission: [actions.LIST, subjects.BUDGET_QUOTE]
    },
    {
      label: 'Empresas Asoc.',
      to: `${basePath}/companies`,
      icon: 'material-symbols:handshake',
      permission: [actions.LIST, subjects.COMPANIES]
    },
    {
      label: 'Mensajes',
      to: `${basePath}/messages`,
      icon: 'material-symbols:chat',
      permission: [actions.LIST, subjects.MESSAGE]
    },
    {section: 'Técnico'},
    {
      label: 'Contratos',
      to: `${basePath}/contracts`,
      icon: 'mdi:file-document-edit-outline',
      permission: [actions.LIST, subjects.CONTRACTS]
    },
    {
      label: 'Ficheros',
      to: `${basePath}/files`,
      icon: 'material-symbols:upload-file',
      permission: [actions.LIST, subjects.FILE]
    },
    {
      label: 'Configuración',
      to: `${basePath}/config`,
      icon: 'material-symbols:edit-document',
      permission: [actions.VIEW, subjects.PROMOTION_CONFIG]
    }
  ]

  const isPromotionSection = computed(() => {
    const normalized = route.path.replace(/\/+$/, '')
    return normalized.startsWith('/promotions/') &&
      normalized !== '/promotions/new' &&
      route.params.id !== undefined
  })

  const navItems = computed(() => {
    if (!isPromotionSection.value) return []
    const promotionId = route.params.id
    const basePath = `/promotions/${promotionId}`
    const allItems = secondaryMenuConfig(basePath)

    return allItems
      .filter(item => {
        if (item.section) return true
        if (!item.permission) return true
        return can(item.permission[0], item.permission[1])
      })
      .map(item => (planningAlert.value.hasAlert && item.to === `${basePath}/planning`
        ? { ...item, badge: true }
        : item))
  })

  const loadPromotions = async () => {
    if (!import.meta.client) return
    if (loadPromise) return loadPromise
    if (sharedPromotions.value.length > 0) return

    loadPromise = (async () => {
      sharedLoadingPromotions.value = true
      try {
        const response = await getPromotions()
        sharedPromotions.value = response.data || []
        if (sharedPromotions.value.length > 0) {
          const promotionIdFromRoute = route.params.id
          sharedSelectedPromotion.value = sharedPromotions.value.find(p => p.id === promotionIdFromRoute) || sharedPromotions.value[0]
        }
      } catch (error) {
        console.error('Error loading promotions:', error)
      } finally {
        sharedLoadingPromotions.value = false
        loadPromise = null
      }
    })()

    return loadPromise
  }

  const openPromotion = (promotion) => {
    navigateTo(`/promotions/${promotion.id}`)
  }

  const reloadPromotions = async () => {
    sharedPromotions.value = []
    sharedSelectedPromotion.value = null
    loadPromise = null
    await loadPromotions()
  }

  const isNavItemActive = (item) => {
    if (!item.to) return false
    const currentPath = route.path
    const itemPath = item.to
    if (item.exact) {
      return currentPath === itemPath
    }
    return currentPath.startsWith(itemPath)
  }

  watch(isPromotionSection, (newValue) => {
    if (import.meta.client && newValue && sharedPromotions.value.length === 0) {
      loadPromotions()
    }
  }, {immediate: true})

  // Sincronizar selectedPromotion con la ruta actual cuando cambiamos de promoción
  watch(() => route.params.id, (newId) => {
    if (newId && sharedPromotions.value.length > 0) {
      const matched = sharedPromotions.value.find(p => p.id === newId)
      if (matched) {
        sharedSelectedPromotion.value = matched
      } else {
        reloadPromotions()
      }
    }
  })

  // Insignia de "presupuesto fuera del Gantt" en la entrada Planificación (INV-G-25):
  // la alerta sigue a la promoción de la ruta y se refresca por socket
  const { alert: planningAlert, trackPromotion } = usePlanningAlert()

  watch([isPromotionSection, () => route.params.id], ([inSection, id]) => {
    trackPromotion(inSection && id ? String(id) : null)
  }, { immediate: true })

  return {
    isPromotionSection,
    navItems,
    promotions: sharedPromotions,
    selectedPromotion: sharedSelectedPromotion,
    loadingPromotions: sharedLoadingPromotions,
    loadPromotions,
    openPromotion,
    reloadPromotions,
    isNavItemActive
  }
}
